import { Time } from '@enums';

/** What the client can tell about a password-reset link before submitting. */
export interface ResetTokenInfo {
  expired: boolean;
  email?: string;
}

/** Tolerance for clock drift between this device and the server. */
const CLOCK_SKEW_SECONDS = 60;
const JWT_PARTS = 3;
const BASE64_BLOCK = 4;

function decodeBase64Url(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padding =
    (BASE64_BLOCK - (base64.length % BASE64_BLOCK)) % BASE64_BLOCK;
  const binary = atob(base64 + '='.repeat(padding));
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Reads a reset JWT's `exp` and `email` claims WITHOUT verifying its
 * signature — the server stays the authority (its 401 on submit is the
 * backstop). Anything that cannot be decoded, or carries no numeric `exp`,
 * is treated as expired so the user is offered a new link.
 */
export function readResetToken(
  token: string | null | undefined,
  now: number = Date.now(),
): ResetTokenInfo {
  if (!token) {
    return { expired: true };
  }
  const parts = token.split('.');
  if (parts.length !== JWT_PARTS) {
    return { expired: true };
  }
  let payload: { exp?: unknown; email?: unknown };
  try {
    payload = JSON.parse(decodeBase64Url(parts[1]));
  } catch {
    return { expired: true };
  }
  if (payload === null || typeof payload !== 'object') {
    return { expired: true };
  }
  const email =
    typeof payload.email === 'string' && payload.email !== ''
      ? payload.email
      : undefined;
  if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
    return { expired: true, email };
  }
  const expired = now / Time.ONE_SECOND > payload.exp + CLOCK_SKEW_SECONDS;
  return { expired, email };
}
